/**
 * @see https://umijs.org/docs/max/access#access
 * 基于 initialState.currentUser 做路由级权限，未登录时所有需登录页面不可访问
 */
type RoleType = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * roleType:
 * 1-管理员 2-清洗公司 3-城管-区县 4-督导 5-游客 6-城市代理人 7-城管-市
 *
 * subRoleTypes:
 * 31-城管-管理员 32-城管-巡查员 33-城管-监测员 34-单车系统管理员 35-单车巡查员
 */
const SHARED_BIKE_ACCESS_KEYS = [
  'sharedBikeOperatorManageMenuAccess',
  'sharedBikeOperatorManageRegisteredVehicleAccess',

  'sharedBikeVehicleManageMenuAccess',
  'sharedBikeVehicleManageRegisteredVehicleAccess',
  'sharedBikeVehicleManageUnregisteredVehicleAccess',

  'sharedBikeParkingZoneManageMenuAccess',
  'sharedBikeParkingZoneManageParkingZoneListAccess',

  'sharedBikeDeviceManageMenuAccess',
  'sharedBikeDeviceManageSnifferListAccess',
  'sharedBikeDeviceManageCameraListAccess',

  'sharedBikeWorkOrderManageMenuAccess',
  'sharedBikeWorkOrderManageWorkOrderListAccess',
  'sharedBikeWorkOrderManageProcessedWorkOrderListAccess',

  'sharedBikePatrolManageMenuAccess',
  'sharedBikePatrolManagePatrolRecordListAccess',

  'sharedBikeHomeWorkbenchAccess',

  'sharedBikeDataMonitorMenuAccess',
  'sharedBikeDataMonitorRealTimeHeatMapAccess',
  'sharedBikeDataMonitorRealTimeParkingMapAccess',
  'sharedBikeDataMonitorRealTimeIllegalParkingMapAccess',
  'sharedBikeDataMonitorRealTimeVehicleMapAccess',

  'sharedBikeWarningManageMenuAccess',
  'sharedBikeWarningManageWarningInfoAccess',
  'sharedBikeWarningManageWarningMapAccess',
  'sharedBikeWarningManageWarningRuleAccess',

  'sharedBikePatrolStaffManageAccess',

  'sharedBikeAccountManageMenuAccess',
  'sharedBikeAccountManageCityManagementAccountListAccess',
  'sharedBikeAccountManageOperatorAccountListAccess',
] as const;

const ALL_ACCESS_KEYS = [
  // 首页：/welcome
  'welcomeAccess',

  // 餐饮数据看板菜单：/dataBoard
  'dataBoardMenuAccess',
  // 数据汇总：/dataBoard/dataSummary
  'dataBoardDataSummaryAccess',
  // 餐饮地图：/dataBoard/cateringMap
  'dataBoardCateringMapAccess',
  // 实时餐饮地图：/dataBoard/realTimeCateringMap
  'dataBoardRealTimeCateringMapAccess',

  // 餐饮单位管理菜单：/restaurantManage
  'restaurantManageMenuAccess',
  // 新增餐饮单位：/restaurantManage/restaurantAdd
  'restaurantManageRestaurantAddAccess',
  // 待审批餐饮单位：/restaurantManage/pendingReviewList
  'restaurantManagePendingReviewListAccess',
  // 已通过餐饮单位：/restaurantManage/approvedList
  'restaurantManageApprovedListAccess',
  // 编辑餐饮单位：/restaurantManage/restaurantEdit
  'restaurantManageRestaurantEditAccess',
  // 餐饮单位列表：/restaurantManage/restaurantList
  'restaurantManageRestaurantListAccess',
  // 已删除餐饮单位：/restaurantManage/restaurantDeleted
  'restaurantManageRestaurantDeletedAccess',

  // 清洗单位管理菜单：/airPurifierManage
  'airPurifierManageMenuAccess',
  // 清洗单位列表：/airPurifierManage/cleaningCompanyList
  'airPurifierManageCleaningCompanyListAccess',
  // 服务餐饮门店：/airPurifierManage/servedRestaurantList
  'airPurifierManageServedRestaurantListAccess',
  // 清洗现场监控：/airPurifierManage/cleaningSiteMonitor
  'airPurifierManageCleaningSiteMonitorAccess',

  // 清洗记录管理菜单：/cleaningRecordManage
  'cleaningRecordManageMenuAccess',
  // 待审核清洗记录：/cleaningRecordManage/pendingReviewList
  'cleaningRecordManagePendingReviewListAccess',
  // 已审核清洗记录：/cleaningRecordManage/approvedList
  'cleaningRecordManageApprovedListAccess',
  // 未清洗餐饮单位：/cleaningRecordManage/unCleanedCateringList
  'cleaningRecordManageUnCleanedCateringListAccess',

  // 油烟监测管理菜单：/smokeMonitorManage
  'smokeMonitorManageMenuAccess',
  // 待审核监测记录：/smokeMonitorManage/pendingReviewList
  'smokeMonitorManagePendingReviewListAccess',
  // 已审核监测记录：/smokeMonitorManage/approvedSmokeMonitorList
  'smokeMonitorManageApprovedSmokeMonitorListAccess',
  // 未监测餐饮单位：/smokeMonitorManage/unCleanedSmokeMonitorList
  'smokeMonitorManageUnCleanedSmokeMonitorListAccess',
  // 在线监测设备：/smokeMonitorManage/onlineMonitoringDevice
  'smokeMonitorManageOnlineMonitoringDeviceAccess',
  // 实时监测分析：/smokeMonitorManage/realTimeMonitoringAnalysis
  'smokeMonitorManageRealTimeMonitoringAnalysisAccess',

  // 巡查检测管理菜单：/inspectionManage
  'inspectionManageMenuAccess',
  // 正常巡查记录：/inspectionManage/normalList
  'inspectionManageNormalListAccess',
  // 异常巡查记录：/inspectionManage/abnormalList
  'inspectionManageAbnormalListAccess',
  // 未巡查餐饮单位：/inspectionManage/unInspectedCateringList
  'inspectionManageUnInspectedCateringListAccess',

  // 净化设备管理菜单：/cleaningDeviceManage
  'cleaningDeviceManageMenuAccess',
  // 净化设备列表：/cleaningDeviceManage/cleaningDeviceList
  'cleaningDeviceManageCleaningDeviceListAccess',

  // 系统账号管理菜单：/accountManage
  'accountManageMenuAccess',
  // 区县管理：/accountManage/districtManagementList
  'accountManageDistrictManagementListAccess',
  // 清洗公司账号：/accountManage/cleaningCompanyAccountList
  'accountManageCleaningCompanyAccountListAccess',
  // 县城管局账号：/accountManage/cityManagementAccountList
  'accountManageCityManagementAccountListAccess',
  // 市城管局账号：/accountManage/sCityManagementAccountList
  'accountManageSCityManagementAccountListAccess',
  // 现场督导账号：/accountManage/supervisorAccountList
  'accountManageSupervisorAccountListAccess',
  // 授权账号管理：/accountManage/authorizedAccountList
  'accountManageAuthorizedAccountListAccess',

  // 通知公告管理菜单：/noticeManage
  'noticeManageMenuAccess',
  // 通知列表：/noticeManage/noticeList
  'noticeManageNoticeListAccess',
  // 公告列表：/noticeManage/announcementList
  'noticeManageAnnouncementListAccess',

  // 投诉/督导管理菜单：/complaintManage
  'complaintManageMenuAccess',
  // 待处理投诉列表：/complaintManage/pendingReviewList
  'complaintManagePendingReviewListAccess',
  // 已处理投诉列表：/complaintManage/processedList
  'complaintManageProcessedListAccess',
  // 待处理督导列表：/complaintManage/pendingSupervisorList
  'complaintManagePendingSupervisorListAccess',
  // 已处理督导列表：/complaintManage/processedSupervisorList
  'complaintManageProcessedSupervisorListAccess',
  // 短信提醒：/complaintManage/advertisingManagement
  'complaintManageAdvertisingManagementAccess',
  // 餐饮单位账号：/accountManage/restaurantAccountList
  'accountManageRestaurantAccountListAccess',

  // 自动化工具菜单：/automationTool
  'automationToolMenuAccess',
  // 图片上传：/automationTool/imageUpload
  'automationToolImageUploadAccess',
  // 清洗记录导入：/automationTool/cleaningRecordImport
  'automationToolCleaningRecordImportAccess',
  // 监测记录导入：/automationTool/monitorRecordImport
  'automationToolMonitorRecordImportAccess',
  // 任务列表：/automationTool/taskList
  'automationToolTaskListAccess',

  // 共享单车管理系统
  ...SHARED_BIKE_ACCESS_KEYS,
] as const;

type AccessKey = (typeof ALL_ACCESS_KEYS)[number];

/**
 * 每个角色独立配置自己的菜单和页面权限。
 *
 * 注意：
 * - 配置父菜单权限不会自动拥有子页面权限。
 * - 需要展示某个菜单并访问其页面时，父菜单和子页面权限都要显式配置。
 * - 需要给某个角色增减页面时，只改对应 roleType 的数组即可。
 */
const ROLE_ACCESS_MAP: Record<RoleType, readonly AccessKey[]> = {
  // roleType 1：管理员
  1: [
    'welcomeAccess', // 首页

    // 餐饮数据看板
    'dataBoardMenuAccess', // 餐饮数据看板菜单
    'dataBoardDataSummaryAccess', // 数据汇总
    'dataBoardCateringMapAccess', // 餐饮地图
    'dataBoardRealTimeCateringMapAccess', // 实时餐饮地图

    // 餐饮单位管理
    'restaurantManageMenuAccess', // 餐饮单位管理菜单
    'restaurantManageRestaurantAddAccess', // 新增餐饮单位
    'restaurantManagePendingReviewListAccess', // 待审批餐饮单位
    'restaurantManageApprovedListAccess', // 已通过餐饮单位
    'restaurantManageRestaurantEditAccess', // 编辑餐饮单位
    'restaurantManageRestaurantListAccess', // 餐饮单位列表
    'restaurantManageRestaurantDeletedAccess', // 已删除餐饮单位

    // 清洗单位管理
    'airPurifierManageMenuAccess', // 清洗单位管理菜单
    'airPurifierManageCleaningCompanyListAccess', // 清洗单位列表
    'airPurifierManageServedRestaurantListAccess', // 服务餐饮门店
    'airPurifierManageCleaningSiteMonitorAccess', // 清洗现场监控

    // 清洗记录管理
    'cleaningRecordManageMenuAccess', // 清洗记录管理菜单
    'cleaningRecordManagePendingReviewListAccess', // 待审核清洗记录
    'cleaningRecordManageApprovedListAccess', // 已审核清洗记录
    'cleaningRecordManageUnCleanedCateringListAccess', // 未清洗餐饮单位

    // 油烟监测管理
    'smokeMonitorManageMenuAccess', // 油烟监测管理菜单
    'smokeMonitorManagePendingReviewListAccess', // 待审核监测记录
    'smokeMonitorManageApprovedSmokeMonitorListAccess', // 已审核监测记录
    'smokeMonitorManageUnCleanedSmokeMonitorListAccess', // 未监测餐饮单位
    'smokeMonitorManageOnlineMonitoringDeviceAccess', // 在线监测设备
    'smokeMonitorManageRealTimeMonitoringAnalysisAccess', // 实时监测分析

    // 巡查检测管理
    'inspectionManageMenuAccess', // 巡查检测管理菜单
    'inspectionManageNormalListAccess', // 正常巡查记录
    'inspectionManageAbnormalListAccess', // 异常巡查记录
    'inspectionManageUnInspectedCateringListAccess', // 未巡查餐饮单位

    // 净化设备管理
    'cleaningDeviceManageMenuAccess', // 净化设备管理菜单
    'cleaningDeviceManageCleaningDeviceListAccess', // 净化设备列表

    // 系统账号管理
    'accountManageMenuAccess', // 系统账号管理菜单
    'accountManageDistrictManagementListAccess', // 区县管理
    'accountManageCleaningCompanyAccountListAccess', // 清洗公司账号
    'accountManageCityManagementAccountListAccess', // 县城管局账号
    'accountManageSCityManagementAccountListAccess', // 市城管局账号
    'accountManageSupervisorAccountListAccess', // 现场督导账号
    'accountManageAuthorizedAccountListAccess', // 授权账号管理
    'accountManageRestaurantAccountListAccess', // 餐饮单位账号

    // 通知公告管理
    'noticeManageMenuAccess', // 通知公告管理菜单
    'noticeManageNoticeListAccess', // 通知列表
    'noticeManageAnnouncementListAccess', // 公告列表

    // 投诉/督导管理
    'complaintManageMenuAccess', // 投诉/督导管理菜单
    'complaintManagePendingReviewListAccess', // 待处理投诉列表
    'complaintManageProcessedListAccess', // 已处理投诉列表
    'complaintManagePendingSupervisorListAccess', // 待处理督导列表
    'complaintManageProcessedSupervisorListAccess', // 已处理督导列表
    'complaintManageAdvertisingManagementAccess', // 短信提醒

    // 自动化工具
    'automationToolMenuAccess', // 自动化工具菜单
    'automationToolImageUploadAccess', // 图片上传
    'automationToolCleaningRecordImportAccess', // 清洗记录导入
    'automationToolMonitorRecordImportAccess', // 监测记录导入
    'automationToolTaskListAccess', // 任务列表

    ...SHARED_BIKE_ACCESS_KEYS,
  ],

  // roleType 2：清洗公司 目前没有权限
  2: [],

  // roleType 3：城管-区县
  3: [
    'welcomeAccess', // 首页

    // 餐饮数据看板
    'dataBoardMenuAccess', // 餐饮数据看板菜单
    'dataBoardDataSummaryAccess', // 数据汇总
    'dataBoardCateringMapAccess', // 餐饮地图
    'dataBoardRealTimeCateringMapAccess', // 实时餐饮地图

    // 餐饮单位管理
    'restaurantManageMenuAccess', // 餐饮单位管理菜单
    'restaurantManageRestaurantAddAccess', // 新增餐饮单位
    'restaurantManagePendingReviewListAccess', // 待审批餐饮单位
    'restaurantManageApprovedListAccess', // 已通过餐饮单位
    'restaurantManageRestaurantEditAccess', // 编辑餐饮单位
    'restaurantManageRestaurantListAccess', // 餐饮单位列表
    'restaurantManageRestaurantDeletedAccess', // 已删除餐饮单位

    // 清洗单位管理
    'airPurifierManageMenuAccess', // 清洗单位管理菜单
    'airPurifierManageCleaningCompanyListAccess', // 清洗单位列表
    'airPurifierManageServedRestaurantListAccess', // 服务餐饮门店
    'airPurifierManageCleaningSiteMonitorAccess', // 清洗现场监控

    // 清洗记录管理
    'cleaningRecordManageMenuAccess', // 清洗记录管理菜单
    'cleaningRecordManagePendingReviewListAccess', // 待审核清洗记录
    'cleaningRecordManageApprovedListAccess', // 已审核清洗记录
    'cleaningRecordManageUnCleanedCateringListAccess', // 未清洗餐饮单位

    // 油烟监测管理
    'smokeMonitorManageMenuAccess', // 油烟监测管理菜单
    'smokeMonitorManagePendingReviewListAccess', // 待审核监测记录
    'smokeMonitorManageApprovedSmokeMonitorListAccess', // 已审核监测记录
    'smokeMonitorManageUnCleanedSmokeMonitorListAccess', // 未监测餐饮单位
    'smokeMonitorManageOnlineMonitoringDeviceAccess', // 在线监测设备
    'smokeMonitorManageRealTimeMonitoringAnalysisAccess', // 实时监测分析

    // 巡查检测管理
    'inspectionManageMenuAccess', // 巡查检测管理菜单
    'inspectionManageNormalListAccess', // 正常巡查记录
    'inspectionManageAbnormalListAccess', // 异常巡查记录
    'inspectionManageUnInspectedCateringListAccess', // 未巡查餐饮单位

    // 净化设备管理
    'cleaningDeviceManageMenuAccess', // 净化设备管理菜单
    'cleaningDeviceManageCleaningDeviceListAccess', // 净化设备列表

    // 系统账号管理
    //'accountManageMenuAccess', // 系统账号管理菜单
    // 'accountManageDistrictManagementListAccess', // 区县管理
    // 'accountManageCleaningCompanyAccountListAccess', // 清洗公司账号
    // 'accountManageCityManagementAccountListAccess', // 县城管局账号
    // 'accountManageSCityManagementAccountListAccess', // 市城管局账号
    // 'accountManageSupervisorAccountListAccess', // 现场督导账号
    // 'accountManageAuthorizedAccountListAccess', // 授权账号管理

    // 通知公告管理
    'noticeManageMenuAccess', // 通知公告管理菜单
    'noticeManageNoticeListAccess', // 通知列表
    'noticeManageAnnouncementListAccess', // 公告列表

    // 投诉/督导管理
    'complaintManageMenuAccess', // 投诉/督导管理菜单
    'complaintManagePendingReviewListAccess', // 待处理投诉列表
    'complaintManageProcessedListAccess', // 已处理投诉列表
    'complaintManagePendingSupervisorListAccess', // 待处理督导列表
    'complaintManageProcessedSupervisorListAccess', // 已处理督导列表
    'complaintManageAdvertisingManagementAccess', // 短信提醒

    ...SHARED_BIKE_ACCESS_KEYS,
  ],

  // roleType 4：督导 目前没有权限
  4: [],

  // roleType 5：游客
  5: [
    'welcomeAccess', // 首页

    // 餐饮数据看板
    'dataBoardMenuAccess', // 餐饮数据看板菜单
    'dataBoardDataSummaryAccess', // 数据汇总
    'dataBoardCateringMapAccess', // 餐饮地图
    'dataBoardRealTimeCateringMapAccess', // 实时餐饮地图

    // 餐饮单位管理
    'restaurantManageMenuAccess', // 餐饮单位管理菜单
    'restaurantManageRestaurantAddAccess', // 新增餐饮单位
    'restaurantManagePendingReviewListAccess', // 待审批餐饮单位
    'restaurantManageApprovedListAccess', // 已通过餐饮单位
    'restaurantManageRestaurantEditAccess', // 编辑餐饮单位

    // 清洗单位管理
    'airPurifierManageMenuAccess', // 清洗单位管理菜单
    'airPurifierManageCleaningCompanyListAccess', // 清洗单位列表
    'airPurifierManageServedRestaurantListAccess', // 服务餐饮门店
    'airPurifierManageCleaningSiteMonitorAccess', // 清洗现场监控

    // 清洗记录管理
    'cleaningRecordManageMenuAccess', // 清洗记录管理菜单
    'cleaningRecordManagePendingReviewListAccess', // 待审核清洗记录
    'cleaningRecordManageApprovedListAccess', // 已审核清洗记录
    'cleaningRecordManageUnCleanedCateringListAccess', // 未清洗餐饮单位

    // 油烟监测管理
    'smokeMonitorManageMenuAccess', // 油烟监测管理菜单
    'smokeMonitorManagePendingReviewListAccess', // 待审核监测记录
    'smokeMonitorManageApprovedSmokeMonitorListAccess', // 已审核监测记录
    'smokeMonitorManageUnCleanedSmokeMonitorListAccess', // 未监测餐饮单位
    'smokeMonitorManageRealTimeMonitoringAnalysisAccess', // 实时监测分析

    // 巡查检测管理
    'inspectionManageMenuAccess', // 巡查检测管理菜单
    'inspectionManageNormalListAccess', // 正常巡查记录
    'inspectionManageAbnormalListAccess', // 异常巡查记录
    'inspectionManageUnInspectedCateringListAccess', // 未巡查餐饮单位

    // 净化设备管理
    'cleaningDeviceManageMenuAccess', // 净化设备管理菜单
    'cleaningDeviceManageCleaningDeviceListAccess', // 净化设备列表

    // 投诉/督导管理
    'complaintManageMenuAccess', // 投诉/督导管理菜单
    'complaintManagePendingReviewListAccess', // 待处理投诉列表
    'complaintManageProcessedListAccess', // 已处理投诉列表
    'complaintManagePendingSupervisorListAccess', // 待处理督导列表
    'complaintManageProcessedSupervisorListAccess', // 已处理督导列表

    ...SHARED_BIKE_ACCESS_KEYS,
  ],

  // roleType 6：城市代理人
  6: [
    'welcomeAccess', // 首页

    // 餐饮数据看板
    'dataBoardMenuAccess', // 餐饮数据看板菜单
    'dataBoardDataSummaryAccess', // 数据汇总
    'dataBoardCateringMapAccess', // 餐饮地图
    'dataBoardRealTimeCateringMapAccess', // 实时餐饮地图

    // 餐饮单位管理
    'restaurantManageMenuAccess', // 餐饮单位管理菜单
    'restaurantManageRestaurantAddAccess', // 新增餐饮单位
    'restaurantManagePendingReviewListAccess', // 待审批餐饮单位
    'restaurantManageApprovedListAccess', // 已通过餐饮单位
    'restaurantManageRestaurantEditAccess', // 编辑餐饮单位
    'restaurantManageRestaurantListAccess', // 餐饮单位列表
    'restaurantManageRestaurantDeletedAccess', // 已删除餐饮单位

    // 清洗单位管理
    'airPurifierManageMenuAccess', // 清洗单位管理菜单
    'airPurifierManageCleaningCompanyListAccess', // 清洗单位列表
    'airPurifierManageServedRestaurantListAccess', // 服务餐饮门店
    'airPurifierManageCleaningSiteMonitorAccess', // 清洗现场监控

    // 清洗记录管理
    'cleaningRecordManageMenuAccess', // 清洗记录管理菜单
    'cleaningRecordManagePendingReviewListAccess', // 待审核清洗记录
    'cleaningRecordManageApprovedListAccess', // 已审核清洗记录
    'cleaningRecordManageUnCleanedCateringListAccess', // 未清洗餐饮单位

    // 油烟监测管理
    'smokeMonitorManageMenuAccess', // 油烟监测管理菜单
    'smokeMonitorManagePendingReviewListAccess', // 待审核监测记录
    'smokeMonitorManageApprovedSmokeMonitorListAccess', // 已审核监测记录
    'smokeMonitorManageUnCleanedSmokeMonitorListAccess', // 未监测餐饮单位
    'smokeMonitorManageRealTimeMonitoringAnalysisAccess', // 实时监测分析
    'smokeMonitorManageOnlineMonitoringDeviceAccess', // 在线监测设备

    // 巡查检测管理
    'inspectionManageMenuAccess', // 巡查检测管理菜单
    'inspectionManageNormalListAccess', // 正常巡查记录
    'inspectionManageAbnormalListAccess', // 异常巡查记录
    'inspectionManageUnInspectedCateringListAccess', // 未巡查餐饮单位

    // 净化设备管理
    'cleaningDeviceManageMenuAccess', // 净化设备管理菜单
    'cleaningDeviceManageCleaningDeviceListAccess', // 净化设备列表

    // 系统账号管理
    'accountManageMenuAccess', // 系统账号管理菜单
    //'accountManageDistrictManagementListAccess', // 区县管理
    'accountManageCleaningCompanyAccountListAccess', // 清洗公司账号
    'accountManageCityManagementAccountListAccess', // 县城管局账号
    //'accountManageSCityManagementAccountListAccess', // 市城管局账号
    'accountManageSupervisorAccountListAccess', // 现场督导账号
    // 'accountManageAuthorizedAccountListAccess', // 授权账号管理

    // 通知公告管理
    'noticeManageMenuAccess', // 通知公告管理菜单
    'noticeManageNoticeListAccess', // 通知列表
    'noticeManageAnnouncementListAccess', // 公告列表

    // 投诉/督导管理
    'complaintManageMenuAccess', // 投诉/督导管理菜单
    'complaintManagePendingReviewListAccess', // 待处理投诉列表
    'complaintManageProcessedListAccess', // 已处理投诉列表
    'complaintManagePendingSupervisorListAccess', // 待处理督导列表
    'complaintManageProcessedSupervisorListAccess', // 已处理督导列表
    'complaintManageAdvertisingManagementAccess', // 短信提醒
    'accountManageRestaurantAccountListAccess', // 餐饮单位账号

    ...SHARED_BIKE_ACCESS_KEYS,
  ],

  // roleType 7：城管-市
  7: [
    'welcomeAccess', // 首页

    // 餐饮数据看板
    'dataBoardMenuAccess', // 餐饮数据看板菜单
    'dataBoardDataSummaryAccess', // 数据汇总
    'dataBoardCateringMapAccess', // 餐饮地图
    'dataBoardRealTimeCateringMapAccess', // 实时餐饮地图

    // 餐饮单位管理
    'restaurantManageMenuAccess', // 餐饮单位管理菜单
    'restaurantManageRestaurantAddAccess', // 新增餐饮单位
    'restaurantManagePendingReviewListAccess', // 待审批餐饮单位
    'restaurantManageApprovedListAccess', // 已通过餐饮单位
    'restaurantManageRestaurantEditAccess', // 编辑餐饮单位
    'restaurantManageRestaurantListAccess', // 餐饮单位列表
    'restaurantManageRestaurantDeletedAccess', // 已删除餐饮单位

    // 清洗单位管理
    'airPurifierManageMenuAccess', // 清洗单位管理菜单
    'airPurifierManageCleaningCompanyListAccess', // 清洗单位列表
    'airPurifierManageServedRestaurantListAccess', // 服务餐饮门店
    'airPurifierManageCleaningSiteMonitorAccess', // 清洗现场监控

    // 清洗记录管理
    'cleaningRecordManageMenuAccess', // 清洗记录管理菜单
    'cleaningRecordManagePendingReviewListAccess', // 待审核清洗记录
    'cleaningRecordManageApprovedListAccess', // 已审核清洗记录
    'cleaningRecordManageUnCleanedCateringListAccess', // 未清洗餐饮单位

    // 油烟监测管理
    'smokeMonitorManageMenuAccess', // 油烟监测管理菜单
    'smokeMonitorManagePendingReviewListAccess', // 待审核监测记录
    'smokeMonitorManageApprovedSmokeMonitorListAccess', // 已审核监测记录
    'smokeMonitorManageUnCleanedSmokeMonitorListAccess', // 未监测餐饮单位
    'smokeMonitorManageRealTimeMonitoringAnalysisAccess', // 实时监测分析
    'smokeMonitorManageOnlineMonitoringDeviceAccess', // 在线监测设备

    // 巡查检测管理
    'inspectionManageMenuAccess', // 巡查检测管理菜单
    'inspectionManageNormalListAccess', // 正常巡查记录
    'inspectionManageAbnormalListAccess', // 异常巡查记录
    'inspectionManageUnInspectedCateringListAccess', // 未巡查餐饮单位

    // 净化设备管理
    'cleaningDeviceManageMenuAccess', // 净化设备管理菜单
    'cleaningDeviceManageCleaningDeviceListAccess', // 净化设备列表

    // 系统账号管理
    //'accountManageMenuAccess', // 系统账号管理菜单
    // 'accountManageDistrictManagementListAccess', // 区县管理
    // 'accountManageCleaningCompanyAccountListAccess', // 清洗公司账号
    // 'accountManageCityManagementAccountListAccess', // 县城管局账号
    // 'accountManageSCityManagementAccountListAccess', // 市城管局账号
    // 'accountManageSupervisorAccountListAccess', // 现场督导账号
    // 'accountManageAuthorizedAccountListAccess', // 授权账号管理

    // 通知公告管理
    'noticeManageMenuAccess', // 通知公告管理菜单
    'noticeManageNoticeListAccess', // 通知列表
    'noticeManageAnnouncementListAccess', // 公告列表

    // 投诉/督导管理
    'complaintManageMenuAccess', // 投诉/督导管理菜单
    'complaintManagePendingReviewListAccess', // 待处理投诉列表
    'complaintManageProcessedListAccess', // 已处理投诉列表
    'complaintManagePendingSupervisorListAccess', // 待处理督导列表
    'complaintManageProcessedSupervisorListAccess', // 已处理督导列表
    'complaintManageAdvertisingManagementAccess', // 短信提醒

    // 自动化工具
    'automationToolMenuAccess', // 自动化工具菜单
    'automationToolImageUploadAccess', // 图片上传
    'automationToolCleaningRecordImportAccess', // 清洗记录导入
    'automationToolMonitorRecordImportAccess', // 监测记录导入

    ...SHARED_BIKE_ACCESS_KEYS,
  ],
};

const createDefaultAccess = () =>
  ALL_ACCESS_KEYS.reduce<Record<AccessKey, boolean>>((accessMap, accessKey) => {
    accessMap[accessKey] = false;
    return accessMap;
  }, {} as Record<AccessKey, boolean>);

const isRoleType = (roleType: unknown): roleType is RoleType =>
  typeof roleType === 'number' && [1, 2, 3, 4, 5, 6, 7].includes(roleType);

const getUserSubRoleTypes = (currentUser?: { subRoleTypes?: number[] }): number[] => {
  if (Array.isArray(currentUser?.subRoleTypes)) {
    return currentUser.subRoleTypes.map(Number);
  }
  try {
    const stored = JSON.parse(localStorage.getItem('subRoleTypes') || '[]');
    return Array.isArray(stored) ? stored.map(Number) : [];
  } catch {
    return [];
  }
};

const SHARED_BIKE_ACCESS_KEY_SET = new Set<string>(SHARED_BIKE_ACCESS_KEYS);

export default function access(initialState: { currentUser?: any } | undefined) {
  const { currentUser } = initialState ?? {};
  const roleType = Number(currentUser?.roleType ?? localStorage.getItem('roleType'));
  const accessMap = createDefaultAccess();
  let accessKeys: AccessKey[] = isRoleType(roleType) ? [...ROLE_ACCESS_MAP[roleType]] : [];

  // roleType=3：按 subRoleTypes 裁剪油烟 / 单车权限
  if (roleType === 3) {
    const subRoleTypes = getUserSubRoleTypes(currentUser);
    if (!subRoleTypes.includes(34)) {
      accessKeys = accessKeys.filter((key) => !SHARED_BIKE_ACCESS_KEY_SET.has(key));
    }
    if (!subRoleTypes.includes(31)) {
      accessKeys = accessKeys.filter((key) => SHARED_BIKE_ACCESS_KEY_SET.has(key));
    }
  }

  accessKeys.forEach((accessKey) => {
    accessMap[accessKey] = true;
  });

  return accessMap;
}
